type Point = { x: number; y: number };
/** Markers closer than this overlap; a row spaces them this far apart, lifted this much. */
const MIN_PX = 34;
const STEP_PX = 34;
const LIFT_PX = 14;

/**
 * Screen offsets that lay overlapping markers out in a row above their group's centre.
 * Groups merge only when every pair across them is `near`, so no chain of overlaps puts two
 * distant sites in one row. Markers in no group get `undefined`.
 */
export function spreadNudges(
  points: Point[],
  { near = () => true }: { near?: (i: number, j: number) => boolean } = {},
): ([number, number] | undefined)[] {
  const parent = points.map((_, i) => i);
  const root = (i: number): number => (parent[i] === i ? i : (parent[i] = root(parent[i])));
  const groups = new Map<number, number[]>(points.map((_, i) => [i, [i]]));
  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      const [ri, rj] = [root(i), root(j)];
      if (ri === rj) continue;
      const overlap = Math.hypot(points[i].x - points[j].x, points[i].y - points[j].y) < MIN_PX;
      const [gi, gj] = [groups.get(ri)!, groups.get(rj)!];
      if (overlap && gi.every((a) => gj.every((b) => near(a, b)))) {
        parent[rj] = ri;
        groups.set(ri, [...gi, ...gj]);
        groups.delete(rj);
      }
    }
  }

  const nudges: ([number, number] | undefined)[] = points.map(() => undefined);
  for (const members of groups.values()) {
    if (members.length < 2) continue;
    // West to east, so the row keeps the sites' own order.
    members.sort((a, b) => points[a].x - points[b].x || a - b);
    const cx = members.reduce((s, i) => s + points[i].x, 0) / members.length;
    const cy = members.reduce((s, i) => s + points[i].y, 0) / members.length;
    members.forEach((i, slot) => {
      const x = cx + (slot - (members.length - 1) / 2) * STEP_PX;
      nudges[i] = [x - points[i].x, cy - LIFT_PX - points[i].y];
    });
  }
  return nudges;
}
